/**
 * A debounced function that also exposes `flush` (run a pending call now,
 * synchronously) and `cancel` (drop a pending call without running it).
 */
export interface DebouncedFunction<Args extends unknown[]> {
  (...args: Args): void
  /** Runs the pending call immediately, if one is scheduled; otherwise a no-op. */
  flush: () => void
  /** Drops the pending call, if one is scheduled, without running it. */
  cancel: () => void
}

/**
 * Creates a debounced function that delays invoking `func` until after `wait` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * @param func The function to debounce.
 * @param wait The number of milliseconds to delay.
 * @returns A new debounced function, with `flush`/`cancel` escape hatches.
 */
export function debounce<Args extends unknown[]>(
  func: (...args: Args) => unknown,
  wait: number,
): DebouncedFunction<Args> {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let pendingArgs: Args | null = null

  const runPending = (): void => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    if (pendingArgs) {
      const args = pendingArgs
      pendingArgs = null
      func(...args)
    }
  }

  const executedFunction = (...args: Args): void => {
    pendingArgs = args
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(runPending, wait)
  }

  executedFunction.flush = runPending

  executedFunction.cancel = (): void => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    pendingArgs = null
  }

  return executedFunction
}
